/**
 * /me/loans/settlement-calc — Session 3d PR 17.
 * Owning agent: Khidmat. What-if early settlement calculator.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { session3dApi } from "../lib/session3d-api";
import {
  MeActionsRow,
  MeEmployeeRequiredGate,
  Session3dMeShell,
  useCurrentEmployeeId,
} from "./_session3dShell";

export function MyLoanSettlementCalcPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  // Read application_id + settlement_date from URL params; default
  // settlement to today + 90 days for a sensible what-if scenario.
  const { applicationId, settlementDate } = useMemo(() => {
    if (typeof window === "undefined") return { applicationId: "", settlementDate: "" };
    const url = new URLSearchParams(window.location.search);
    const today = new Date();
    today.setDate(today.getDate() + 90);
    return {
      applicationId: url.get("application_id") ?? "",
      settlementDate: url.get("settlement_date") ?? today.toISOString().slice(0, 10),
    };
  }, []);

  const query = useQuery({
    queryKey: ["s3d-loan-settlement-calc", employeeId, applicationId, settlementDate],
    queryFn: () =>
      session3dApi.loanSettlementCalc(employeeId, applicationId, settlementDate),
    enabled: !!employeeId && !!applicationId,
  });

  const data = query.data;

  return (
    <MeEmployeeRequiredGate>
      <Session3dMeShell
        title={t("me.loan-settlement-calc.title", "Early Settlement Calculator")}
        subtitle={t(
          "me.loan-settlement-calc.subtitle",
          "What-if early-settlement calculator with discount estimate.",
        )}
        briefContext={{
          total: 1,
          attention: 0,
          critical: 0,
          top_kind: t("me.loan-settlement-calc.title", "Early Settlement"),
          window_label: t("me.loan-settlement-calc.window", "any date"),
        }}
        actionsRow={
          <MeActionsRow
            khidmatPrefill={t(
              "me.loan-settlement-calc.ask_khidmat",
              "What would my early settlement be if I pay next month?",
            )}
          />
        }
      >
        {!applicationId && (
          <div className="rounded-2xl border border-dashed border-border-subtle bg-surface-sunken/40 p-6 text-sm text-text-secondary">
            {t(
              "me.loan-settlement-calc.pick_loan",
              "Pick a loan from My Active Loans first — settlement-calc opens from each loan's row action.",
            )}
          </div>
        )}
        {applicationId && query.isLoading && <Loading />}
        {applicationId && query.isError && (
          <ErrorPanel message={(query.error as Error).message} />
        )}
        {applicationId && data && (
          <div className="rounded-2xl border border-border-subtle bg-white p-4 text-sm">
            <p className="text-xs text-text-tertiary">
              {t(
                "me.loan-settlement-calc.note",
                "Outstanding + early-payment discount + final amount. Submit routes to formal settlement via M16.9.",
              )}
            </p>
            <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
                  {t("me.loan-settlement-calc.kpi.outstanding", "Outstanding")}
                </div>
                <div className="font-mono text-base font-semibold">
                  {data.outstanding_at_date_sar.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
                  {t("me.loan-settlement-calc.kpi.discount", "Discount")}
                </div>
                <div className="font-mono text-base font-semibold text-emerald-700">
                  {data.early_payment_discount_sar.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
                  {t("me.loan-settlement-calc.kpi.final", "Final settlement")}
                </div>
                <div className="font-mono text-base font-bold text-brand-deep">
                  {data.final_settlement_sar.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-text-tertiary">{data.trace_text}</p>
          </div>
        )}
      </Session3dMeShell>
    </MeEmployeeRequiredGate>
  );
}
